import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking in React frontend
 * This should be called in index.tsx before rendering the app
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.NODE_ENV || 'development';
  const release = process.env.REACT_APP_SENTRY_RELEASE || '1.0.0';

  // Only initialize Sentry if DSN is provided
  if (!dsn) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Sentry DSN not provided. Error tracking disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Filter out health check and static assets
    // Note: Unhandled promise rejections are automatically captured in browser SDK
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Don't ignore network errors - we want to track them
    ],
    beforeSend(event: any, hint?: any) {
      // Filter out health check errors
      if (event.request?.url?.includes('/health')) {
        return null;
      }
      // Add more context to network errors
      if (event.exception) {
        const error = hint?.originalException || hint?.syntheticException;
        if (error && error.message && error.message.includes('Failed to fetch')) {
          event.tags = {
            ...event.tags,
            errorType: 'network_error',
          };
        }
      }
      return event;
    },
  });

  // Sentry initialized silently
}

/**
 * Helper function to capture errors with context
 */
export function captureError(error: Error | string, context?: Record<string, any>) {
  if (typeof error === 'string') {
    Sentry.captureMessage(error, {
      level: 'error',
      extra: context,
    });
  } else {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

/**
 * Helper function to capture network errors
 */
export function captureNetworkError(url: string, status: number, error: Error | string) {
  Sentry.captureException(typeof error === 'string' ? new Error(error) : error, {
    tags: {
      errorType: 'network_error',
      httpStatus: status,
    },
    extra: {
      url,
      status,
    },
  });
}

